import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { Loader } from 'lucide-react';
import { auth, googleProvider } from './firebase';
import { signInWithPopup, signOut, onAuthStateChanged, getAdditionalUserInfo } from 'firebase/auth';
import { trackEvent, trackPageview } from './utils/analytics';
import { getCurrentMonthKey, normalizeMonthKey, formatMonthKey } from './utils/dateUtils';
import { exportToJSON } from './utils/exportData';
import testData from './testData.json';
import {
  getOfflineUser,
  getStorageMode,
  setStorageMode,
  getAllOfflineData,
  hasOfflineData,
  clearOfflineData
} from './utils/localStorage';
import { AccountStorageFactory } from './services/AccountStorageFactory';
import { ExpenseStorageFactory } from './services/ExpenseStorageFactory';
import { createAccount, createMonthlyEntry, createDefaultFireSettings } from './models/AccountModels';
import Header from './components/Header';
import BottomNav from './components/BottomNav';
import AboutModal from './components/AboutModal';
import AccountManager from './components/AccountManager';
import MonthPicker from './components/MonthPicker';
import EntryView from './views/EntryView';
import HistoryView from './views/HistoryView';
import ExpensesView from './views/ExpensesView';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import { usePageMeta } from './hooks/usePageMeta';
import Home from './pages/Home';

// Check if test mode is enabled
const TEST_MODE = process.env.REACT_APP_TEST_MODE === 'true';

// Zero-pads every entry's month key and collapses any duplicate that results
// (keeping the most recently saved one), so legacy unpadded keys like
// "2026-6" can't sort out of order against "2026-07".
const normalizeMonthlyData = (monthlyData) => {
  const byMonth = new Map();
  monthlyData.forEach(entry => {
    const month = normalizeMonthKey(entry.month);
    const existing = byMonth.get(month);
    if (!existing || (entry.timestamp || '') >= (existing.timestamp || '')) {
      byMonth.set(month, { ...entry, month });
    }
  });
  return [...byMonth.values()].sort((a, b) => a.month.localeCompare(b.month));
};

// Backfills fireSettings on accounts loaded from storage written before the
// FIRE feature existed, so downstream code never has to null-check it.
const withFireDefaults = (accounts) => ({
  assets: accounts?.assets || [],
  debts: accounts?.debts || [],
  fireSettings: accounts?.fireSettings || createDefaultFireSettings()
});

// A category is optional, so an empty string is valid — only returns null
// (after alerting) when it's too long or contains unsafe characters.
const validateCategory = (raw) => {
  const trimmed = raw.trim();
  if (trimmed.length > 30) {
    alert('Category must be less than 30 characters');
    return null;
  }
  if (/<|>|<script>/i.test(trimmed)) {
    alert('Category contains invalid characters');
    return null;
  }
  return trimmed;
};

const FinanceTracker = () => {
  usePageMeta({
    title: 'Your Dashboard - Monthly Wealth Tracker',
    description: 'Manage your accounts and track your net worth.',
    path: '/app',
    noindex: true,
  });

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [storageMode, setStorageModeState] = useState(null); // 'offline' or 'cloud'
  const [showMigrationPrompt, setShowMigrationPrompt] = useState(false);

  // Auth state listener
  useEffect(() => {
    // If test mode is enabled, bypass auth and load test data
    if (TEST_MODE) {
      setUser({
        uid: 'test-user',
        email: 'test@example.com',
        displayName: 'Test User'
      });
      loadTestData();
      setLoading(false);
      return;
    }

    // Check for saved storage mode preference
    const savedMode = getStorageMode();

    if (savedMode === 'offline') {
      // User previously chose offline mode
      const offlineUser = getOfflineUser();
      setUser(offlineUser);
      setStorageModeState('offline');
      // Data will be loaded by the useEffect when storage service is ready
      return;
    }

    // Listen for Firebase auth changes (cloud mode)
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        setStorageModeState('cloud');

        // Check if user has offline data to migrate
        if (hasOfflineData()) {
          setShowMigrationPrompt(true);
        }
        // Data will be loaded by the useEffect when storage service is ready
      } else {
        setUser(null);
        setStorageModeState(null);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const [accounts, setAccounts] = useState({
    assets: [],
    debts: []
  });

  const [monthlyData, setMonthlyData] = useState([]);

  // Create storage service instance based on mode
  const accountStorageService = useMemo(
    () => AccountStorageFactory.createService(storageMode, user),
    [storageMode, user]
  );
  const expenseStorageService = useMemo(
    () => ExpenseStorageFactory.createService(storageMode, user),
    [storageMode, user]
  );
  const [currentView, setCurrentView] = useState('entry');
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [editMode, setEditMode] = useState(false);
  // The month key an in-progress edit originally loaded from, so we can
  // relocate (rather than duplicate) the record if the user changes the
  // month/year mid-edit instead of re-entering all the values.
  const [editingSourceMonth, setEditingSourceMonth] = useState(null);
  const [entryData, setEntryData] = useState({});
  const [showAccountManager, setShowAccountManager] = useState(false);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [newAccountName, setNewAccountName] = useState('');
  const [newAccountCategory, setNewAccountCategory] = useState('');
  const [addingType, setAddingType] = useState(null);
  const [saving, setSaving] = useState(false);

  // Load data when storage service is ready
  useEffect(() => {
    if (TEST_MODE) return; // Test data is loaded separately
    if (!user || !storageMode) return;

    const loadData = async () => {
      try {
        const data = await accountStorageService.loadData();
        setAccounts(withFireDefaults(data.accounts));
        setMonthlyData(normalizeMonthlyData(data.monthlyData));
      } catch (error) {
        console.error('Error loading data:', error);
        alert('Error loading your data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [accountStorageService, user, storageMode]);

  // Load test data from local JSON file (for testing without authentication)
  const loadTestData = () => {
    try {
      setAccounts(withFireDefaults(testData.accounts));
      setMonthlyData(normalizeMonthlyData(testData.monthlyData));
    } catch (error) {
      console.error('Error loading test data:', error);
    }
  };

  // Update accounts and save with debouncing
  useEffect(() => {
    if (TEST_MODE) return; // Skip saving in test mode
    if (!user || !storageMode) return;

    if (accounts.assets.length > 0 || accounts.debts.length > 0) {
      const timer = setTimeout(async () => {
        try {
          await accountStorageService.saveAccounts(accounts);
        } catch (error) {
          console.error('Error saving accounts:', error);
        }
      }, 1000);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, user, storageMode]);

  // Update monthly data and save with debouncing
  useEffect(() => {
    if (TEST_MODE) return; // Skip saving in test mode
    if (!user || !storageMode) return;

    if (monthlyData.length > 0) {
      const timer = setTimeout(async () => {
        try {
          await accountStorageService.saveMonthlyData(monthlyData);
        } catch (error) {
          console.error('Error saving monthly data:', error);
        }
      }, 1000);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthlyData, user, storageMode]);

  const handleGoogleSignIn = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      trackEvent(getAdditionalUserInfo(result)?.isNewUser ? 'signed_up' : 'signed_in', { method: 'google' });
      setStorageMode('cloud');
    } catch (error) {
      console.error('Error signing in:', error);
      alert('Error signing in. Please try again.');
    }
  };

  const handleOfflineMode = () => {
    // Offline mode has no server-side record, so unlike Google sign-in we
    // can't tell a first-timer from someone returning after a sign-out —
    // this fires every time the mode-selection screen leads to offline.
    trackEvent('offline_mode_selected');
    const offlineUser = getOfflineUser();
    setUser(offlineUser);
    setStorageModeState('offline');
    setStorageMode('offline');
    // Data will be loaded by the useEffect when storage service is ready
  };

  const handleSignOut = async () => {
    try {
      // In test mode, just reload the page to reset state
      if (TEST_MODE) {
        window.location.reload();
        return;
      }

      trackEvent('signed_out');

      if (storageMode === 'offline') {
        // Clear offline mode and reset state
        setStorageMode(null);
        setUser(null);
        setStorageModeState(null);
        setAccounts(withFireDefaults());
        setMonthlyData([]);
        setCurrentView('entry');
      } else {
        // Sign out from Google
        await signOut(auth);
        setStorageMode(null);
        setStorageModeState(null);
        setAccounts(withFireDefaults());
        setMonthlyData([]);
        setCurrentView('entry');
      }
    } catch (error) {
      console.error('Error signing out:', error);
      alert('Error signing out. Please try again.');
    }
  };

  const handleMigrateData = async () => {
    if (!user || storageMode !== 'cloud') return;

    try {
      const offlineData = getAllOfflineData();

      // Merge offline data with cloud data
      const mergedAccounts = {
        assets: [...accounts.assets, ...offlineData.accounts.assets].filter((account, index, self) =>
          index === self.findIndex((a) => a.name === account.name)
        ),
        debts: [...accounts.debts, ...offlineData.accounts.debts].filter((account, index, self) =>
          index === self.findIndex((a) => a.name === account.name)
        ),
        // Prefer whichever side actually has expenses configured; cloud wins ties.
        fireSettings: (accounts.fireSettings?.annualExpenses ? accounts.fireSettings : null)
          || (offlineData.accounts.fireSettings?.annualExpenses ? offlineData.accounts.fireSettings : null)
          || createDefaultFireSettings()
      };

      const mergedMonthlyData = normalizeMonthlyData([...monthlyData, ...offlineData.monthlyData]);

      // Save merged data using storage service
      await accountStorageService.saveAccounts(mergedAccounts);
      await accountStorageService.saveMonthlyData(mergedMonthlyData);

      // Update state
      setAccounts(mergedAccounts);
      setMonthlyData(mergedMonthlyData);

      // Clear offline data
      clearOfflineData();
      setShowMigrationPrompt(false);

      alert('Your offline data has been successfully migrated to cloud storage!');
    } catch (error) {
      console.error('Error migrating data:', error);
      alert('Error migrating data. Please try again.');
    }
  };

  const handleExportData = () => {
    if (monthlyData.length === 0 && accounts.assets.length === 0 && accounts.debts.length === 0) {
      alert('No data to export. Please add accounts and enter some monthly data first.');
      return;
    }

    try {
      exportToJSON(accounts, monthlyData);
      trackEvent('data_exported');
      alert('Data exported successfully!');
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Error exporting data. Please try again.');
    }
  };

  const initializeEntryData = (monthKey = null) => {
    const key = monthKey || getCurrentMonthKey();
    const existingData = monthlyData.find(m => m.month === key);

    if (existingData) {
      setEntryData(existingData.values);
    } else {
      const initial = {};
      [...accounts.assets, ...accounts.debts].forEach(account => {
        initial[account.name] = '';
      });
      setEntryData(initial);
    }
    setSelectedMonth(key);
    setEditingSourceMonth(key);
  };

  const handleValueChange = (accountName, value) => {
    // Prevent negative numbers
    const numValue = parseFloat(value);
    if (value !== '' && numValue < 0) {
      return;
    }

    // Prevent extremely large numbers
    if (numValue > 1000000000000) {
      alert('Amount is too large');
      return;
    }

    setEntryData(prev => ({
      ...prev,
      [accountName]: value
    }));
  };

  const addAccount = (type) => {
    if (!newAccountName.trim()) return;

    // Validate account name
    const trimmedName = newAccountName.trim();

    if (trimmedName.length === 0) {
      alert('Account name cannot be empty');
      return;
    }

    if (trimmedName.length > 50) {
      alert('Account name must be less than 50 characters');
      return;
    }

    // Check for dangerous characters
    if (/<|>|<script>/i.test(trimmedName)) {
      alert('Account name contains invalid characters');
      return;
    }

    // Check for duplicates
    const existingAccounts = type === 'asset' ? accounts.assets : accounts.debts;
    if (existingAccounts.some(acc => acc.name === trimmedName)) {
      alert('An account with this name already exists');
      return;
    }

    // Validate category
    const trimmedCategory = validateCategory(newAccountCategory);
    if (trimmedCategory === null) return;

    const newAccount = createAccount(trimmedName, type, trimmedCategory);

    setAccounts(prev => ({
      ...prev,
      [type === 'asset' ? 'assets' : 'debts']: [...prev[type === 'asset' ? 'assets' : 'debts'], newAccount]
    }));
    trackEvent('account_added', { type });

    setNewAccountName('');
    setNewAccountCategory('');
    setAddingType(null);
  };

  const updateAccountCategory = (accountName, type, category) => {
    const trimmedCategory = validateCategory(category);
    if (trimmedCategory === null) return;

    setAccounts(prev => ({
      ...prev,
      [type === 'asset' ? 'assets' : 'debts']: prev[type === 'asset' ? 'assets' : 'debts'].map(a =>
        a.name === accountName ? { ...a, category: trimmedCategory } : a
      )
    }));
  };

  const updateFireSettings = (updates) => {
    setAccounts(prev => ({
      ...prev,
      fireSettings: { ...prev.fireSettings, ...updates }
    }));
  };

  // Toggles whether an asset counts toward the FIRE number (e.g. a kid's
  // 529 plan shouldn't). Debts always count in full, so this only applies
  // to assets.
  const toggleFireAsset = (accountName) => {
    setAccounts(prev => ({
      ...prev,
      assets: prev.assets.map(a =>
        a.name === accountName ? { ...a, includeInFire: a.includeInFire === false } : a
      )
    }));
  };

  const removeAccount = (accountName, type) => {
    if (!window.confirm(`Are you sure you want to remove "${accountName}"? It will be hidden from new entries but preserved in historical data.`)) {
      return;
    }

    setAccounts(prev => ({
      ...prev,
      [type === 'asset' ? 'assets' : 'debts']: prev[type === 'asset' ? 'assets' : 'debts'].filter(a => a.name !== accountName)
    }));
    trackEvent('account_removed', { type });

    if (editMode) {
      setEntryData(prev => {
        const newData = { ...prev };
        delete newData[accountName];
        return newData;
      });
    }
  };

  const saveMonthData = async () => {
    if (!selectedMonth) {
      alert('Please select a month first!');
      return;
    }

    setSaving(true);

    const totalAssets = accounts.assets.reduce((sum, account) => {
      const value = parseFloat(entryData[account.name]) || 0;
      return sum + value;
    }, 0);

    const totalDebts = accounts.debts.reduce((sum, account) => {
      const value = parseFloat(entryData[account.name]) || 0;
      return sum + value;
    }, 0);

    const netWorth = totalAssets - totalDebts;

    const newData = createMonthlyEntry(
      selectedMonth,
      { ...entryData },
      totalAssets,
      totalDebts,
      netWorth
    );

    const updatedMonthlyData = (() => {
      // If the entry was moved to a different month/year mid-edit, drop the
      // old record so it doesn't linger alongside the relocated one.
      const base = editingSourceMonth && editingSourceMonth !== selectedMonth
        ? monthlyData.filter(m => m.month !== editingSourceMonth)
        : monthlyData;

      const existing = base.findIndex(m => m.month === selectedMonth);
      if (existing >= 0) {
        const updated = [...base];
        updated[existing] = newData;
        return updated;
      }
      return [...base, newData].sort((a, b) => a.month.localeCompare(b.month));
    })();

    trackEvent('month_entry_saved', {
      is_new_entry: !monthlyData.some(m => m.month === selectedMonth),
      account_count: accounts.assets.length + accounts.debts.length,
    });
    setMonthlyData(updatedMonthlyData);

    // Storage service will auto-save via useEffect
    setSaving(false);
    setEditMode(false);
    setSelectedMonth(null);
    setEditingSourceMonth(null);
    alert('Data saved successfully!');
  };

  const startNewEntry = (monthKey = null) => {
    const key = monthKey || getCurrentMonthKey();
    setSelectedMonth(key);
    initializeEntryData(key);
    setEditMode(true);
    setShowMonthPicker(false);
  };

  // Changes which month an in-progress edit will save under, without
  // touching the values already typed in (unlike startNewEntry, which
  // would reload/reset entryData for the target month).
  const changeEntryMonth = (newMonthKey) => {
    if (newMonthKey === selectedMonth) {
      setShowMonthPicker(false);
      return;
    }
    const targetHasOtherData = monthlyData.some(
      m => m.month === newMonthKey && m.month !== editingSourceMonth
    );
    if (targetHasOtherData) {
      const proceed = window.confirm(
        `${formatMonthKey(newMonthKey)} already has saved data. Moving this entry there will overwrite it. Continue?`
      );
      if (!proceed) return;
    }
    setSelectedMonth(newMonthKey);
    setShowMonthPicker(false);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setSelectedMonth(null);
    setEditingSourceMonth(null);
  };

  // Login screen
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-card border border-gray-100 p-8">
          <div className="text-center mb-8">
            <img src="/logo.png" alt="" className="w-12 h-12 mx-auto mb-4 rounded-xl" />
            <h1 className="text-gray-900 font-semibold text-lg tracking-tight">Monthly Wealth Tracker</h1>
            <p className="text-gray-500 text-sm mt-1">Track your net worth over time</p>
          </div>

          <div className="space-y-3">
            <button
              onClick={handleGoogleSignIn}
              className="w-full flex items-center justify-center gap-3 px-5 py-3 bg-white text-gray-700 text-sm font-medium border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors shadow-sm"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in with Google
            </button>

            <button
              onClick={handleOfflineMode}
              className="w-full px-5 py-3 border border-gray-200 rounded-xl text-gray-500 text-sm hover:text-gray-900 hover:bg-gray-50 transition-colors"
            >
              Continue Offline
            </button>
          </div>

          <p className="text-center text-xs text-gray-400 mt-8">
            By continuing you agree to our{' '}
            <Link to="/privacy" className="text-gray-500 hover:text-gray-900 transition-colors underline">Privacy Policy</Link>
            {' & '}
            <Link to="/terms" className="text-gray-500 hover:text-gray-900 transition-colors underline">Terms</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20 sm:pb-24">
      <Header
        user={user}
        showMenu={showMenu}
        setShowMenu={setShowMenu}
        setShowAccountManager={setShowAccountManager}
        setShowAbout={setShowAbout}
        handleSignOut={handleSignOut}
        handleExportData={handleExportData}
      />

      <div className="px-4 sm:px-8 py-8 sm:py-10 max-w-5xl mx-auto">
        {currentView === 'entry' && (
          <EntryView
            accounts={accounts}
            monthlyData={monthlyData}
            selectedMonth={selectedMonth}
            editMode={editMode}
            entryData={entryData}
            saving={saving}
            setShowAccountManager={setShowAccountManager}
            startNewEntry={startNewEntry}
            setShowMonthPicker={setShowMonthPicker}
            saveMonthData={saveMonthData}
            cancelEdit={cancelEdit}
            handleValueChange={handleValueChange}
            fireSettings={accounts.fireSettings}
            updateFireSettings={updateFireSettings}
            toggleFireAsset={toggleFireAsset}
          />
        )}
        {currentView === 'history' && (
          <HistoryView
            monthlyData={monthlyData}
            initializeEntryData={initializeEntryData}
            setSelectedMonth={setSelectedMonth}
            setEditMode={setEditMode}
            setCurrentView={setCurrentView}
          />
        )}
        {currentView === 'expenses' && (
          <ExpensesView expenseStorageService={expenseStorageService} />
        )}
      </div>

      <BottomNav currentView={currentView} setCurrentView={setCurrentView} />

      {showAccountManager && (
        <AccountManager
          accounts={accounts}
          setShowAccountManager={setShowAccountManager}
          addingType={addingType}
          setAddingType={setAddingType}
          newAccountName={newAccountName}
          setNewAccountName={setNewAccountName}
          newAccountCategory={newAccountCategory}
          setNewAccountCategory={setNewAccountCategory}
          addAccount={addAccount}
          removeAccount={removeAccount}
          updateAccountCategory={updateAccountCategory}
        />
      )}
      {showMonthPicker && (
        <MonthPicker
          monthlyData={monthlyData}
          setShowMonthPicker={setShowMonthPicker}
          onSelectMonth={editMode ? changeEntryMonth : startNewEntry}
          getCurrentMonthKey={getCurrentMonthKey}
        />
      )}
      {showAbout && <AboutModal setShowAbout={setShowAbout} />}

      {/* Migration Prompt Modal */}
      {showMigrationPrompt && (
        <div className="fixed inset-0 bg-gray-900/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 max-w-md w-full">
            <h2 className="text-gray-900 font-semibold text-lg tracking-tight mb-3">Migrate Offline Data?</h2>
            <p className="text-gray-500 text-sm mb-6">
              Data stored locally on your device was detected. Migrate it to your cloud account to merge with existing data.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleMigrateData}
                className="flex-1 bg-accent text-white rounded-xl px-4 py-2.5 text-sm font-medium hover:bg-emerald-700 transition-colors shadow-sm"
              >
                Migrate
              </button>
              <button
                onClick={() => setShowMigrationPrompt(false)}
                className="flex-1 border border-gray-200 rounded-xl text-gray-500 hover:text-gray-900 hover:bg-gray-50 px-4 py-2.5 text-sm transition-colors"
              >
                Skip
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const HomeRoute = () => {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const savedMode = getStorageMode();

    if (savedMode === 'offline') {
      navigate('/app', { replace: true });
      return;
    }

    if (savedMode === 'cloud') {
      const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
        unsubscribe();
        if (currentUser) {
          navigate('/app', { replace: true });
        } else {
          setChecking(false);
        }
      });
      return () => unsubscribe();
    }

    setChecking(false);
  }, [navigate]);

  if (checking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return <Home />;
};

const RouteTracker = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageview(location.pathname);
  }, [location.pathname]);
  return null;
};

const App = () => {
  return (
    <>
      <RouteTracker />
      <Routes>
        <Route path="/" element={<HomeRoute />} />
        <Route path="/app" element={<FinanceTracker />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
      </Routes>
    </>
  );
};

export default App;
